import * as Auth from '../lib/Auth'
import * as Api from '../lib/Api'
import { authActions } from '../store/signIn'
import { dataActions } from '../store/data'
import { store } from '../store'
import { decodeToken } from '../lib/Global'
import { sortBy } from '../utils/global'

/**
 * Call to login auth server endpoint
 *
 * @param username
 * @param password
 * @returns {Promise<void>}
 */
export const login = async (username, password, rememberMe, redirect = '/field_forms') => {
  try {
    const response = await Auth.login(username, password)
    store.dispatch(authActions.login(response))
    const tokenInfo = decodeToken(response.access_token)
    const user = await Api.getUser()
    // check if it's parent company
    if (!user?.belongs_to_parent_company) {
      store.dispatch(authActions.logout())
      // eslint-disable-next-line no-throw-literal
      throw {
        name: 'Invalid company',
        error: 'There are no permissions for this company',
        code: 402
      }
    }

    // check if it's management company
    if (user?.company_type !== 'management') {
      store.dispatch(authActions.logout())
      // eslint-disable-next-line no-throw-literal
      throw {
        name: 'Invalid company',
        error: 'There are no permissions for this company',
        code: 402
      }
    }

    // check roles/access
    const managementRoles = await Api.getManagementRoles()
    const userRole = user?.roles
    let userRoleIndex = -1
    if (!userRole) {
      store.dispatch(authActions.logout())
      // eslint-disable-next-line no-throw-literal
      throw {
        name: 'Invalid company',
        error: 'There are no permissions for this company',
        code: 402
      }
    } else {
      userRoleIndex = managementRoles.findIndex(
        (role) => role.id === userRole
      )
      if (userRoleIndex === -1) {
        store.dispatch(authActions.logout())
        // eslint-disable-next-line no-throw-literal
        throw {
          name: 'Invalid user',
          error: 'There are no permissions for this user',
          code: 402
        }
      }
    }

    store.dispatch(
      authActions.setUserRole(managementRoles[userRoleIndex])
    )

    store.dispatch(
      authActions.setUser({
        ...tokenInfo,
        userInfo: user
      })
    )

    if (rememberMe) {
      store.dispatch(authActions.setRemember(username))
    } else {
      const authStore = store.getState().auth
      if (authStore.email) {
        store.dispatch(authActions.removeRemember())
      }
    }

    store.dispatch(
      authActions.setRedirect({
        redirect
      })
    )

    // get trades and save trades
    const trades = await Api.getTrades()
    const sortedTrades = trades.trades.sort((a, b) => sortBy(a, b, 'name'))
    store.dispatch(dataActions.setTrades(sortedTrades))

    // wo types and save types
    const wotypes = await Api.getWorkOrderTypes()
    const sortedTypes = wotypes.work_order_types.sort((a, b) => sortBy(a, b, 'name'))
    store.dispatch(dataActions.setWorkOrderTypes(sortedTypes))
  } catch (e) {
    console.error(e)
    throw e
  }
}
/**
 * Call to refresh token endpoint
 *
 * @param refreshToken
 * @returns {Promise<void>}
 */
export const refreshToken = async (refreshToken, userId) => {
  const response = await Auth.refreshToken(refreshToken, userId)
  if (response) {
    store.dispatch(
      authActions.setToken({
        ...response
      })
    )
  }
}

/**
 * Check if the logged in user has authorization for specific scopes
 *
 * @param scopes
 * @returns {boolean}
 */
export const userHasAuthorization = scopes => {
  const request = scopes.split(':')
  const authStore = store.getState().auth
  const allowedScopes = authStore.user?.userInfo?.scopes
  const requestedScope = allowedScopes?.permissions[request[0]]

  if (requestedScope && requestedScope[request[1]]) {
    return true
  }
  return false
}

/**
 * Request reset password
 *
 * @param email
 * @returns {Promise<*>}
 */
export const requestResetPassword = async email => {
  try {
    const response = await Api.resetPasswordRequest(email)

    if (response) {
      return response
    } else {
      return { status: false }
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

/**
 * Reset password
 *
 * @param email
 * @returns {Promise<*>}
 */
export const resetPassword = async (
  selector,
  token,
  password,
  password2,
  apiToken
) => {
  try {
    const response = await Auth.resetPassword(
      selector,
      token,
      password,
      password2,
      apiToken
    )

    if (response) {
      return response
    } else {
      return { status: false }
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}
